import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useUnsavedChangesProtection } from "../../../hooks/useUnsavedChangesProtection";
import UnsavedChangesDialog from "../../common/UnsavedChangesDialog";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Autocomplete,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Link as LinkIcon,
  Business as BusinessIcon,
  AccessTime as AccessTimeIcon,
  Category as CategoryIcon,
  ContentCopy as ContentCopyIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Warning as WarningIcon,
  Description as DescriptionIcon,
  AccountTree as AccountTreeIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AppDispatch, RootState } from "../../../redux/store";
import UserAccessControlEditor from "./UserAccessControlEditor";
import {
  fetchAssessment,
  updateAssessment,
  deleteAssessment,
  fetchAssessmentLinks,
  createAssessmentLink,
  updateAssessmentLink,
  fetchAssessedSessions,
  fetchAssessmentHistory,
  updateAssessmentFormSchema,
} from "../../../redux/actions/assessmentActions";
import { fetchAllAgentConfigs } from "../../../redux/actions/agentConfigActions";
import AssessedSessionsTable from "./AssessedSessionsTable";
import AssessmentHistory from "./AssessmentHistory";
import EditLinkDialog from "./EditLinkDialog";
import AssetPicker from "./AssetPicker";
import InterviewPlanEditor from "./InterviewPlanEditor";
import FeatureInsightsConfigEditor, {
  FeatureInsightsConfig,
} from "./FeatureInsightsConfigEditor";
import { ControlGraphEditor } from "./control-graph-editor";
import { getAgentConfigDisplayName } from "../../../utils/render";

const AssessmentView: React.FC = () => {
  const { assessmentId, organizationId } = useParams<{
    assessmentId: string;
    organizationId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  
  // Ref for the assessed sessions section
  const assessedSessionsRef = useRef<HTMLDivElement>(null);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedResourceKey, setCopiedResourceKey] = useState<boolean>(false);

  const handleCopyLinkWithFeedback = (linkId: string, url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedLinkId(linkId);
    setTimeout(() => setCopiedLinkId(null), 2000);
  };

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCopyResourceKey = (resourceKey: string) => {
    navigator.clipboard.writeText(resourceKey);
    setCopiedResourceKey(true);
    setTimeout(() => setCopiedResourceKey(false), 2000);
  };

  const {
    currentAssessment,
    loading,
    error,
    assessmentLinks,
    linksLoading,
    assessedSessions,
    sessionsLoading,
    assessmentHistory,
    historyLoading,
  } = useSelector((state: RootState) => state.assessments);
  const { agentConfigs } = useSelector(
    (state: RootState) => state.agentConfigs
  );

  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createLinkDialogOpen, setCreateLinkDialogOpen] = useState(false);
  const [editWarningDialogOpen, setEditWarningDialogOpen] = useState(false);
  const [deleteFormDialogOpen, setDeleteFormDialogOpen] = useState(false);
  const [controlGraphEditorOpen, setControlGraphEditorOpen] = useState(false);
  const [selectedAgentConfigId, setSelectedAgentConfigId] = useState("");
  const [linkDisplayName, setLinkDisplayName] = useState("");
  const [linkDisplayNameTouched, setLinkDisplayNameTouched] = useState(false);
  const [testOverrideHumanDescription, setTestOverrideHumanDescription] =
    useState("");
  const [editingLink, setEditingLink] = useState<any | null>(null);
  const [additionalContext, setTestAdditionalContext] = useState("");
  const [userAccessControlEnabled, setUserAccessControlEnabled] =
    useState(false);
  const [maxSessionsEnabled, setMaxSessionsEnabled] = useState(false);
  const [maxSessionsPerUser, setMaxSessionsPerUser] = useState<number>(1);
  const [selectedNamespaceAssetRegistry, setSelectedNamespaceAssetRegistry] =
    useState<{
      namespace_assets: Record<string, string[]>;
    } | null>(null);

  const [emailsEnabled, setEmailsEnabled] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [domainsEnabled, setDomainsEnabled] = useState(false);
  const [domains, setDomains] = useState<string[]>([]);

  // Feature Insights Config state for create dialog
  const [createInsightsConfig, setCreateInsightsConfig] =
    useState<FeatureInsightsConfig | null>(null);

  // Control graph key for create/edit link dialogs
  const [selectedControlGraphKey, setSelectedControlGraphKey] = useState<
    string | null
  >(null);

  const [formData, setFormData] = useState({
    scenario_description: "",
    scenario_category: "",
    scenario_applicant: "",
    environment: "",
    context: "",
    time_limit_minutes: 30,
    expected_time_minutes: 15,
    ask_for_questions_before_start: undefined as boolean | undefined,
    discussion_topic: "",
    scenario_goal: "",
    scenario_evaluator: "",
    no_include: "",
    assessment_processing_type: "memory" as "memory" | "form", // definition from memory/assessments/assessed_session.py
  });
  const [editedInterviewPlan, setEditedInterviewPlan] = useState<any>(null);
  const [hasActualChanges, setHasActualChanges] = useState(false);
  const [isSavingAndNavigating, setIsSavingAndNavigating] = useState(false);

  // Use unsaved changes protection
  const { showDialog, confirmNavigation, cancelNavigation } =
    useUnsavedChangesProtection({
      hasUnsavedChanges:
        isEditing && hasActualChanges && !isSavingAndNavigating,
      message:
        "You have unsaved changes to this assessment. Are you sure you want to leave?",
      onConfirmLeave: () => {
        setIsEditing(false);
        setHasActualChanges(false);
      },
    });

  useEffect(() => {
    if (assessmentId && organizationId) {
      // Reset the saving flag when assessment changes
      setIsSavingAndNavigating(false);
      setIsEditing(false);
      setHasActualChanges(false);

      dispatch(fetchAssessment({ assessmentId, organizationId }));
      dispatch(fetchAllAgentConfigs());
      dispatch(fetchAssessmentLinks({ assessmentId, organizationId }));
      dispatch(fetchAssessedSessions({ assessmentId, organizationId }));
    }
    return () => {
      // Clean up when component unmounts
      setIsSavingAndNavigating(false);
    };
  }, [dispatch, assessmentId, organizationId]);

  // Handle scrolling to assessed sessions section and filtering by chat session ID
  useEffect(() => {
    // Check if we have a hash to scroll to
    if (location.hash === '#assessed-sessions' && assessedSessionsRef.current) {
      // Small delay to ensure the component is fully rendered
      setTimeout(() => {
        assessedSessionsRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [location.hash, assessedSessions]);

  useEffect(() => {
    if (currentAssessment) {
      setFormData({
        scenario_description: currentAssessment.scenario_description || "",
        scenario_category: currentAssessment.scenario_category || "",
        scenario_applicant: currentAssessment.scenario_applicant || "",
        environment: currentAssessment.environment || "",
        context: currentAssessment.context || "",
        time_limit_minutes: currentAssessment.time_limit_minutes || 30,
        expected_time_minutes: currentAssessment.expected_time_minutes || 15,
        ask_for_questions_before_start:
          currentAssessment.ask_for_questions_before_start,
        discussion_topic: currentAssessment.discussion_topic || "",
        scenario_goal: currentAssessment.scenario_goal || "",
        scenario_evaluator: currentAssessment.scenario_evaluator || "",
        no_include: currentAssessment.no_include || "",
        assessment_processing_type:
          currentAssessment.assessment_processing_type,
      });

      // Fetch assessment history if we have a resource_key
      if (currentAssessment.resource_key && currentAssessment.organization_id) {
        dispatch(
          fetchAssessmentHistory({
            resourceKey: currentAssessment.resource_key,
            organizationId: currentAssessment.organization_id,
          })
        );
      }
    }
  }, [currentAssessment, dispatch]);

  const handleSave = async () => {
    if (!assessmentId) return;

    if (!editedInterviewPlan || !hasActualChanges) {
      setIsEditing(false);
      toast.info("No changes were made");
      return;
    }

    try {
      if (!currentAssessment) {
        toast.error("Current assessment not found");
        return;
      }
      const result = await dispatch(
        updateAssessment({
          id: assessmentId,
          interview_plan: editedInterviewPlan,
          organization_id: currentAssessment.organization_id,
        })
      ).unwrap();

      // The backend creates a new assessment version, so redirect to it
      if (result.id && result.id !== assessmentId) {
        toast.success(
          "Assessment updated successfully! Redirecting to new version..."
        );

        // Clear all state BEFORE navigation to prevent the modal
        setIsSavingAndNavigating(true); // Set flag first
        setIsEditing(false); // Clear editing state
        setHasActualChanges(false); // Clear changes flag
        setEditedInterviewPlan(null); // Clear edited plan

        // Small delay to ensure state updates are processed
        setTimeout(() => {
          navigate(
            `/dashboard/assessments/${result.organization_id}/${result.id}`,
            { replace: true }
          );
        }, 0);
      } else {
        toast.success("Assessment updated successfully!");
        setIsEditing(false);
        setHasActualChanges(false);
        // Refresh the current assessment to get the latest data
        if (organizationId) {
          dispatch(fetchAssessment({ assessmentId, organizationId }));
        }
      }
    } catch {
      toast.error("Failed to update assessment");
    }
  };

  const handleDelete = async () => {
    if (!assessmentId) return;

    try {
      await dispatch(deleteAssessment(assessmentId)).unwrap();
      toast.success("Assessment deleted successfully!");
      navigate("/dashboard/assessments");
    } catch {
      toast.error("Failed to delete assessment");
    }
  };

  const handleCancel = () => {
    if (currentAssessment) {
      setFormData({
        scenario_description: currentAssessment.scenario_description || "",
        scenario_category: currentAssessment.scenario_category || "",
        scenario_applicant: currentAssessment.scenario_applicant || "",
        environment: currentAssessment.environment || "",
        context: currentAssessment.context || "",
        time_limit_minutes: currentAssessment.time_limit_minutes || 30,
        expected_time_minutes: currentAssessment.expected_time_minutes || 15,
        ask_for_questions_before_start:
          currentAssessment.ask_for_questions_before_start,
        discussion_topic: currentAssessment.discussion_topic || "",
        scenario_goal: currentAssessment.scenario_goal || "",
        scenario_evaluator: currentAssessment.scenario_evaluator || "",
        no_include: currentAssessment.no_include || "",
        assessment_processing_type:
          currentAssessment.assessment_processing_type,
      });
    }
    setEditedInterviewPlan(null); // Reset the edited plan
    setHasActualChanges(false); // Reset the changes flag
    setIsEditing(false);
  };

  const normalizeDomain = (d: string) => {
    const trimmed = (d || "").trim();
    return trimmed.startsWith("@") ? trimmed.slice(1) : trimmed;
  };

  const handleCreateLink = async () => {
    if (!assessmentId || !selectedAgentConfigId) {
      toast.error("Please select an agent configuration");
      return;
    }
    if (!currentAssessment) {
      toast.error("Current assessment not found");
      return;
    }

    const consumptionPolicy = userAccessControlEnabled
      ? (() => {
          const policy: Record<string, any> = {};

          if (maxSessionsEnabled) {
            policy.max_num_converted_sessions_per_user = Math.max(
              1,
              Math.floor(maxSessionsPerUser || 1)
            );
          }

          if (emailsEnabled) {
            const cleanedEmails = (emails || [])
              .map((e) => e.trim())
              .filter(Boolean);
            if (cleanedEmails.length > 0) {
              policy.email_allowlist = cleanedEmails;
            }
          }

          if (domainsEnabled) {
            const cleanedDomains = (domains || [])
              .map(normalizeDomain)
              .map((d) => d.trim())
              .filter(Boolean);
            if (cleanedDomains.length > 0) {
              policy.email_domains_allowlist = cleanedDomains;
            }
          }

          return Object.keys(policy).length > 0 ? policy : null;
        })()
      : null;

    try {
      await dispatch(
        createAssessmentLink({
          assessmentId,
          organizationId: currentAssessment.organization_id,
          agent_config_id: selectedAgentConfigId,
          display_name: linkDisplayName || undefined,
          test_override_human_description: testOverrideHumanDescription || "",
          additional_context: additionalContext || "",
          control_graph_key: selectedControlGraphKey,
          namespace_asset_registry: selectedNamespaceAssetRegistry,
          consumption_policy: consumptionPolicy,
          insights_config: createInsightsConfig,
        })
      ).unwrap();
      toast.success("Assessment link created successfully!");
      // Refresh the assessment links list
      dispatch(
        fetchAssessmentLinks({
          assessmentId,
          organizationId: currentAssessment.organization_id,
        })
      );
      setCreateLinkDialogOpen(false);
    } catch {
      toast.error("Failed to create assessment link");
    }
  };

  const handleDeleteForm = async () => {
    if (!assessmentId || !currentAssessment) return;

    try {
      await dispatch(
        updateAssessmentFormSchema({
          assessment_id: assessmentId,
          form_output_schema: null,
          organization_id: currentAssessment.organization_id,
        })
      ).unwrap();
      toast.success("Form schema deleted successfully!");
      setDeleteFormDialogOpen(false);
      // Refresh the assessment to get updated data
      dispatch(
        fetchAssessment({
          assessmentId,
          organizationId: currentAssessment.organization_id,
        })
      );
    } catch {
      toast.error("Failed to delete form schema");
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">Error: {error}</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard/assessments")}
          sx={{ mt: 2 }}
        >
          Back to Assessments
        </Button>
      </Box>
    );
  }

  if (!currentAssessment) {
    return (
      <Box p={3}>
        <Typography>Assessment not found</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard/assessments")}
          sx={{ mt: 2 }}
        >
          Back to Assessments
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate("/dashboard/assessments")}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">Assessment Details</Typography>
        </Box>
        <Box display="flex" gap={2}>
          {!isEditing ? (
            <>
              <Button
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={() => {
                  setCreateLinkDialogOpen(true);
                  // Mark as touched immediately since it starts empty
                  setLinkDisplayNameTouched(true);
                }}
                disabled={currentAssessment.generation_status === "pending"}
              >
                Create Link
              </Button>
              <Button
                variant="outlined"
                startIcon={<AccountTreeIcon />}
                onClick={() => setControlGraphEditorOpen(true)}
                disabled={currentAssessment.generation_status === "pending"}
                color="secondary"
              >
                Edit Control Graph
              </Button>
              {currentAssessment.is_active !== false &&
                currentAssessment.generation_status !== "pending" && (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </Button>
                )}
            </>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => {
                  if (!editedInterviewPlan || !hasActualChanges) {
                    setIsEditing(false);
                    toast.info("No changes were made");
                  } else {
                    setEditWarningDialogOpen(true);
                  }
                }}
                disabled={loading}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Inactive Assessment Banner */}
        {currentAssessment.is_active === false &&
          currentAssessment.generation_status === "done" && (
            <Grid item xs={12}>
              <Card sx={{ bgcolor: "warning.light", mb: 2 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <WarningIcon color="warning" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        This is an inactive assessment version
                      </Typography>
                      <Typography variant="body2">
                        This assessment has been superseded by a newer version.
                        You cannot edit inactive assessments.
                        {currentAssessment.resource_key && (
                          <>
                            {" "}
                            View the assessment history below to see all
                            versions.
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

        {/* Metadata Card */}
        <Grid item xs={12}>
          <Card elevation={1}>
            <CardContent>
              {currentAssessment.display_title && (
                <Box mb={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {currentAssessment.display_title}
                  </Typography>
                </Box>
              )}
              <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                <Box display="flex" alignItems="center" gap={1}>
                  <BusinessIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Organization:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {currentAssessment.organization_id}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box display="flex" alignItems="center" gap={1}>
                  <CategoryIcon fontSize="small" color="action" />
                  <Chip
                    label={formData.scenario_category}
                    size="small"
                    color={
                      formData.scenario_category === "interview"
                        ? "primary"
                        : "secondary"
                    }
                  />
                </Box>
                {currentAssessment.category && (
                  <>
                    <Divider orientation="vertical" flexItem />
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="text.secondary">
                        Type:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {currentAssessment.category}
                      </Typography>
                    </Box>
                  </>
                )}
                <Chip
                  label={formData.environment}
                  size="small"
                  variant="outlined"
                  color={
                    formData.environment === "production"
                      ? "error"
                      : formData.environment === "staging"
                      ? "warning"
                      : "default"
                  }
                />
                <Box sx={{ flexGrow: 1 }} />
                {currentAssessment.generation_status !== "pending" && (
                  <>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AccessTimeIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Expected: {formData.expected_time_minutes} min | Limit:{" "}
                        {formData.time_limit_minutes} min
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                  </>
                )}
                <Box display="flex" alignItems="center" gap={1}>
                  {currentAssessment.generation_status === "done" ? (
                    <>
                      <CheckCircleIcon fontSize="small" color="success" />
                      <Typography
                        variant="body2"
                        color="success.main"
                        fontWeight="medium"
                      >
                        Generated
                      </Typography>
                    </>
                  ) : currentAssessment.generation_status === "pending" ? (
                    <>
                      <HourglassEmptyIcon fontSize="small" color="warning" />
                      <Typography
                        variant="body2"
                        color="warning.main"
                        fontWeight="medium"
                      >
                        Processing
                      </Typography>
                    </>
                  ) : (
                    <>
                      <RadioButtonUncheckedIcon
                        fontSize="small"
                        color="action"
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight="medium"
                      >
                        Not Started
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
              <Box mt={2}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: "monospace" }}
                  >
                    ID: {currentAssessment.id}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleCopyId(currentAssessment.id)}
                    sx={{ p: 0.25 }}
                  >
                    {copiedId === currentAssessment.id ? (
                      <CheckCircleIcon sx={{ fontSize: 14 }} color="success" />
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    )}
                  </IconButton>
                </Box>
                {currentAssessment.resource_key && (
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace" }}
                    >
                      Resource Key: {currentAssessment.resource_key}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleCopyResourceKey(currentAssessment.resource_key!)
                      }
                      sx={{ p: 0.25 }}
                    >
                      {copiedResourceKey ? (
                        <CheckCircleIcon
                          sx={{ fontSize: 14 }}
                          color="success"
                        />
                      ) : (
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      )}
                    </IconButton>
                  </Box>
                )}
                {currentAssessment.display_title && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Title: {currentAssessment.display_title}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Created:{" "}
                  {new Date(currentAssessment.creation_time).toLocaleString()} |
                  Modified:{" "}
                  {new Date(currentAssessment.modified_time).toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Form Schema Card - Show if processing type is form */}
        {currentAssessment.assessment_processing_type === "form" && (
          <Grid item xs={12}>
            <Card elevation={1}>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Form Schema
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentAssessment.form_output_schema
                        ? `This assessment has a structured form with ${
                            Object.keys(
                              currentAssessment.form_output_schema?.sections ||
                                {}
                            ).length
                          } sections`
                        : "No form schema configured for this assessment"}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={2}>
                    <Button
                      variant="contained"
                      startIcon={<DescriptionIcon />}
                      onClick={() =>
                        navigate(
                          `/dashboard/assessments/${organizationId}/${assessmentId}/form`
                        )
                      }
                    >
                      {currentAssessment.form_output_schema
                        ? "View Form"
                        : "Create Form"}
                    </Button>
                    {currentAssessment.form_output_schema && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setDeleteFormDialogOpen(true)}
                      >
                        Delete
                      </Button>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Interview Links - Moved to top */}
        <Grid item xs={12}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Shareable Interview Links
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {linksLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
              </Box>
            ) : assessmentLinks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No links have been created for this assessment yet.
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 300, overflow: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontSize: "0.8rem",
                          p: 0.75,
                          width: 120,
                          bgcolor: "background.paper",
                        }}
                      >
                        Link ID
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.8rem",
                          p: 0.75,
                          width: 150,
                          bgcolor: "background.paper",
                        }}
                      >
                        Link Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.8rem",
                          p: 0.75,
                          width: 140,
                          bgcolor: "background.paper",
                        }}
                      >
                        Agent Config
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.8rem",
                          p: 0.75,
                          width: 120,
                          bgcolor: "background.paper",
                        }}
                      >
                        Control Graph
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.8rem",
                          p: 0.75,
                          width: 60,
                          bgcolor: "background.paper",
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={0.5}>
                          ACL
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.8rem",
                          p: 0.75,
                          width: 100,
                          bgcolor: "background.paper",
                        }}
                      >
                        Assets
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.8rem",
                          p: 0.75,
                          width: 80,
                          bgcolor: "background.paper",
                        }}
                      >
                        Created
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.8rem",
                          p: 0.75,
                          width: 80,
                          bgcolor: "background.paper",
                        }}
                      >
                        Main
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.8rem",
                          p: 0.75,
                          width: 80,
                          bgcolor: "background.paper",
                        }}
                      >
                        Test
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.8rem",
                          p: 0.75,
                          width: 40,
                          bgcolor: "background.paper",
                        }}
                      >
                        {/* Empty header for Edit column */}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assessmentLinks.map((assessmentLink) => {
                      const agentConfig = agentConfigs.find(
                        (config) => config.id === assessmentLink.agent_config_id
                      );
                      const mainLinkUrl = `https://pixels.persona-ai.ai/login?res_link_params_id=${assessmentLink.id}&org_id=${currentAssessment.organization_id}`;
                      const testLinkUrl = `https://stagingpixels.persona-ai.ai/login?res_link_params_id=${assessmentLink.id}&org_id=${currentAssessment.organization_id}`;
                      return (
                        <TableRow key={assessmentLink.id}>
                          <TableCell sx={{ p: 0.75, maxWidth: 120 }}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.75rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  flex: 1,
                                }}
                                title={assessmentLink.id}
                              >
                                {assessmentLink.id}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleCopyId(assessmentLink.id)}
                                sx={{ p: 0.25, flexShrink: 0 }}
                              >
                                {copiedId === assessmentLink.id ? (
                                  <CheckCircleIcon
                                    sx={{ fontSize: 14 }}
                                    color="success"
                                  />
                                ) : (
                                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ p: 0.75, maxWidth: 150 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.75rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={assessmentLink.display_name || "-"}
                            >
                              {assessmentLink.display_name || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: 0.75, maxWidth: 140 }}>
                            {agentConfig && agentConfig.id ? (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() =>
                                  navigate(
                                    `/dashboard/agent-configs/${agentConfig.id}`
                                  )
                                }
                                sx={{
                                  textTransform: "none",
                                  p: 0.25,
                                  fontSize: "0.75rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  justifyContent: "flex-start",
                                  minWidth: 0,
                                  display: "block",
                                  textAlign: "left",
                                  width: "100%",
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    textDecoration: "underline",
                                  },
                                }}
                                title={getAgentConfigDisplayName({
                                  display_name: agentConfig.display_name,
                                  bot_name: agentConfig.bot_name,
                                  id: agentConfig.id,
                                })}
                              >
                                {getAgentConfigDisplayName({
                                  display_name: agentConfig.display_name,
                                  bot_name: agentConfig.bot_name,
                                  id: agentConfig.id,
                                })}
                              </Button>
                            ) : assessmentLink.agent_config_id ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  fontSize: "0.8rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={assessmentLink.agent_config_id}
                              >
                                {assessmentLink.agent_config_id}
                              </Typography>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  fontSize: "0.8rem",
                                }}
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ p: 0.75, maxWidth: 120 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.75rem",
                                fontFamily: "monospace",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: assessmentLink.control_graph_key ? "text.primary" : "text.secondary",
                              }}
                              title={assessmentLink.control_graph_key || "Using assessment default"}
                            >
                              {assessmentLink.control_graph_key || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: 0.75 }}>
                            {assessmentLink.consumption_policy ? (
                              <CheckCircleIcon
                                sx={{
                                  fontSize: 18,
                                  color: "success.main",
                                }}
                              />
                            ) : (
                              <CancelIcon
                                sx={{
                                  fontSize: 18,
                                  color: "error.main",
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ p: 0.75, maxWidth: 150 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.8rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={
                                !assessmentLink.namespace_asset_registry
                                  ? "No assets configured"
                                  : Object.keys(
                                      assessmentLink.namespace_asset_registry
                                    ).length === 0
                                  ? "Explicitly empty (0 namespaces)"
                                  : Object.values(
                                      assessmentLink.namespace_asset_registry
                                        .namespace_assets
                                    )
                                      .flat()
                                      .join(", ")
                              }
                            >
                              {!assessmentLink.namespace_asset_registry
                                ? "-"
                                : Object.keys(
                                    assessmentLink.namespace_asset_registry
                                  ).length === 0
                                ? "0"
                                : Object.values(
                                    assessmentLink.namespace_asset_registry
                                      .namespace_assets
                                  )
                                    .flat()
                                    .join(", ")}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: 0.75 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.8rem" }}
                            >
                              {new Date(
                                assessmentLink.created_at
                              ).toLocaleDateString("en-US", {
                                month: "2-digit",
                                day: "2-digit",
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: 0.75 }}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleCopyLinkWithFeedback(
                                  `main-${assessmentLink.id}`,
                                  mainLinkUrl
                                )
                              }
                              title="Copy main link"
                              color="primary"
                              sx={{ p: 0.5 }}
                            >
                              {copiedLinkId === `main-${assessmentLink.id}` ? (
                                <CheckCircleIcon
                                  sx={{ fontSize: 18 }}
                                  color="success"
                                />
                              ) : (
                                <ContentCopyIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ p: 0.75 }}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleCopyLinkWithFeedback(
                                  `test-${assessmentLink.id}`,
                                  testLinkUrl
                                )
                              }
                              title="Copy test link"
                              color="secondary"
                              sx={{ p: 0.5 }}
                            >
                              {copiedLinkId === `test-${assessmentLink.id}` ? (
                                <CheckCircleIcon
                                  sx={{ fontSize: 18 }}
                                  color="success"
                                />
                              ) : (
                                <ContentCopyIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ p: 0.75 }}>
                            <IconButton
                              size="small"
                              onClick={() => setEditingLink(assessmentLink)}
                              title="Edit link"
                              sx={{
                                p: 0.5,
                                transition: "background-color 0.2s",
                                "&:hover": {
                                  bgcolor: "action.hover",
                                },
                              }}
                            >
                              <EditIcon
                                sx={{
                                  fontSize: 18,
                                  color: "primary.main",
                                }}
                              />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
          <EditLinkDialog
            open={!!editingLink}
            onClose={() => setEditingLink(null)}
            link={editingLink}
            onSave={async (updatedLink) => {
              try {
                await dispatch(
                  updateAssessmentLink({
                    linkId: updatedLink.id,
                    ...updatedLink,
                  })
                ).unwrap();
                toast.success("Link updated successfully!");
                setEditingLink(null);
                // Refresh the links list
                dispatch(
                  fetchAssessmentLinks({
                    assessmentId: assessmentId || "",
                    organizationId: currentAssessment.organization_id,
                  })
                );
              } catch {
                toast.error("Failed to update link");
              }
            }}
            organizationId={currentAssessment.organization_id}
          />
        </Grid>

        {/* Processing Indicator */}
        {currentAssessment?.generation_status === "pending" && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
              >
                <CircularProgress size={48} />
                <Typography variant="h6" color="text.secondary">
                  Generating Interview Content
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we process your assessment and generate the
                  interview questions (You will need to refresh to see updates
                  to the status)...
                </Typography>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Generated Content - Only show when status is done */}
        {currentAssessment?.generation_status === "done" &&
          currentAssessment?.payload && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Generated Interview Content
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <InterviewPlanEditor
                  interviewPlan={{
                    scenario: {
                      description: currentAssessment.scenario_description || "",
                      goal: currentAssessment.scenario_goal || "",
                      category: currentAssessment.scenario_category || "",
                      applicant: currentAssessment.scenario_applicant || "",
                      evaluator: currentAssessment.scenario_evaluator || "",
                      no_include: currentAssessment.no_include || "",
                    },
                    eval_criteria: currentAssessment.evaluation_criteria || [],
                    questions:
                      currentAssessment.generated_questions?.map((q: any) => {
                        return {
                          question: {
                            id: q.id || `q_${Math.random()}`,
                            question: q.topic || q.initial_question || "",
                            question_type: q.question_type || "general",
                            would_interviewer_know_answer: false,
                            is_question_multi_part_explanation: false,
                            would_answer_overlap_another_question: false,
                          },
                          dimensions: q.criteria || [],
                          rubric: q.rubric || [], // Use structured rubric directly from API
                        };
                      }) || [],
                    editor_fields: {
                      greeting:
                        currentAssessment.payload?.greeting_cue?.channels
                          ?.intent?.hints?.[0]?.value ||
                        currentAssessment.payload?.greeting ||
                        "",
                      num_questions:
                        currentAssessment.payload?.key_questions?.length || 0,
                      expected_time_minutes:
                        currentAssessment.expected_time_minutes || 15,
                      time_limit_minutes:
                        currentAssessment.time_limit_minutes || 30,
                      ask_for_questions_before_start:
                        currentAssessment.ask_for_questions_before_start ??
                        true,
                      allowed_qa_topics:
                        currentAssessment.allowed_qa_topics || undefined,
                      post_interview_steps:
                        currentAssessment.post_interview_steps || [],
                      check_for_suspicious:
                        currentAssessment.check_for_suspicious !== undefined
                          ? currentAssessment.check_for_suspicious
                          : true,
                      check_for_seeking_clarification:
                        currentAssessment.check_for_seeking_clarification !==
                        undefined
                          ? currentAssessment.check_for_seeking_clarification
                          : true,
                      max_num_user_queries_per_question:
                        currentAssessment.max_num_user_queries_per_question !==
                        undefined
                          ? currentAssessment.max_num_user_queries_per_question
                          : 3,
                      max_num_poorly_answered_questions:
                        currentAssessment.max_num_poorly_answered_questions !==
                        undefined
                          ? currentAssessment.max_num_poorly_answered_questions
                          : 2,
                      min_words_for_chat:
                        currentAssessment.min_words_for_chat !== undefined
                          ? currentAssessment.min_words_for_chat
                          : 0,
                      max_words_for_chat:
                        currentAssessment.max_words_for_chat !== undefined
                          ? currentAssessment.max_words_for_chat
                          : 0,
                      additional_guidance:
                        currentAssessment.additional_guidance || [],
                      post_insight_timeout_seconds:
                        currentAssessment.post_insight_timeout_seconds !==
                        undefined
                          ? currentAssessment.post_insight_timeout_seconds
                          : -2.0,
                      post_statement_timeout_seconds:
                        currentAssessment.post_statement_timeout_seconds !==
                        undefined
                          ? currentAssessment.post_statement_timeout_seconds
                          : -2.0,
                      without_guidance:
                        currentAssessment.without_guidance || "",
                      controversial_content_config:
                        currentAssessment.controversial_content_config || null,
                    },
                    discussion_topic: currentAssessment.discussion_topic || "",
                    criteria_context: currentAssessment.context || "",
                    assessment_processing_type:
                      formData.assessment_processing_type,
                    configuration:
                      currentAssessment.interview_configuration || {
                        min_num_turns: 5,
                        max_num_turns: 10,
                        min_num_questions_answered: 3,
                        non_job: false,
                        positive_feedback: true,
                        reflective_responses: true,
                      },
                  }}
                  onChange={(updatedPlan: any) => {
                    if (isEditing) {
                      // Only set editedInterviewPlan if there are actual changes
                      const originalPlanStr = JSON.stringify({
                        scenario: {
                          description:
                            currentAssessment.scenario_description || "",
                          goal: currentAssessment.scenario_goal || "",
                          category: currentAssessment.scenario_category || "",
                          applicant: currentAssessment.scenario_applicant || "",
                          evaluator: currentAssessment.scenario_evaluator || "",
                          no_include: currentAssessment.no_include || "",
                        },
                        eval_criteria:
                          currentAssessment.evaluation_criteria || [],
                        questions:
                          currentAssessment.generated_questions?.map(
                            (q: any) => {
                              return {
                                question: {
                                  id: q.id || `q_${Math.random()}`,
                                  question: q.topic || q.initial_question || "",
                                  question_type: q.question_type || "general",
                                  would_interviewer_know_answer: false,
                                  is_question_multi_part_explanation: false,
                                  would_answer_overlap_another_question: false,
                                },
                                dimensions: q.criteria || [],
                                rubric: q.rubric || [], // Use structured rubric directly from API
                              };
                            }
                          ) || [],
                        editor_fields: {
                          greeting:
                            currentAssessment.payload?.greeting_cue?.channels
                              ?.intent?.hints?.[0]?.value ||
                            currentAssessment.payload?.greeting ||
                            "",
                          num_questions:
                            currentAssessment.payload?.key_questions?.length ||
                            0,
                          expected_time_minutes:
                            currentAssessment.expected_time_minutes || 15,
                          ask_for_questions_before_start:
                            currentAssessment.ask_for_questions_before_start ??
                            true,
                          time_limit_minutes:
                            currentAssessment.time_limit_minutes || 30,
                          allowed_qa_topics:
                            currentAssessment.allowed_qa_topics || undefined,
                          post_interview_steps:
                            currentAssessment.post_interview_steps || [],
                          check_for_suspicious:
                            currentAssessment.check_for_suspicious !== undefined
                              ? currentAssessment.check_for_suspicious
                              : true,
                          check_for_seeking_clarification:
                            currentAssessment.check_for_seeking_clarification !==
                            undefined
                              ? currentAssessment.check_for_seeking_clarification
                              : true,
                          max_num_user_queries_per_question:
                            currentAssessment.max_num_user_queries_per_question !==
                            undefined
                              ? currentAssessment.max_num_user_queries_per_question
                              : 3,
                          max_num_poorly_answered_questions:
                            currentAssessment.max_num_poorly_answered_questions !==
                            undefined
                              ? currentAssessment.max_num_poorly_answered_questions
                              : 2,
                          min_words_for_chat:
                            currentAssessment.min_words_for_chat !== undefined
                              ? currentAssessment.min_words_for_chat
                              : 0,
                          max_words_for_chat:
                            currentAssessment.max_words_for_chat !== undefined
                              ? currentAssessment.max_words_for_chat
                              : 0,
                          additional_guidance:
                            currentAssessment.additional_guidance || [],
                          post_insight_timeout_seconds:
                            currentAssessment.post_insight_timeout_seconds !==
                            undefined
                              ? currentAssessment.post_insight_timeout_seconds
                              : -2.0,
                          post_statement_timeout_seconds:
                            currentAssessment.post_statement_timeout_seconds !==
                            undefined
                              ? currentAssessment.post_statement_timeout_seconds
                              : -2.0,
                          without_guidance:
                            currentAssessment.without_guidance || "",
                          controversial_content_config:
                            currentAssessment.controversial_content_config ||
                            null,
                        },
                        discussion_topic:
                          currentAssessment.discussion_topic || "",
                        criteria_context: currentAssessment.context || "",
                        assessment_processing_type:
                          currentAssessment.assessment_processing_type,
                        configuration:
                          currentAssessment.interview_configuration || {
                            min_num_turns: 5,
                            max_num_turns: 10,
                            min_num_questions_answered: 3,
                            non_job: false,
                            positive_feedback: true,
                            reflective_responses: true,
                          },
                      });

                      const updatedPlanStr = JSON.stringify(updatedPlan);

                      if (originalPlanStr !== updatedPlanStr) {
                        setEditedInterviewPlan(updatedPlan);
                        setHasActualChanges(true);
                      } else {
                        setEditedInterviewPlan(null);
                        setHasActualChanges(false);
                      }
                    }
                  }}
                  isEditing={isEditing}
                />
              </Paper>
            </Grid>
          )}

        {/* Assessment Sessions - After generated content */}
        <Grid item xs={12} ref={assessedSessionsRef} id="assessed-sessions">
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Assessment Sessions
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <AssessedSessionsTable
              assessedSessions={assessedSessions}
              sessionsLoading={sessionsLoading}
              assessmentId={assessmentId || ""}
              processingType={currentAssessment.assessment_processing_type}
              initialChatSessionFilter={new URLSearchParams(location.search).get('chat_session_id') || undefined}
            />
          </Paper>
        </Grid>

        {/* Assessment History - Show if we have a resource_key */}
        {currentAssessment.resource_key && (
          <Grid item xs={12}>
            <AssessmentHistory
              currentAssessmentId={currentAssessment.id}
              assessmentHistory={assessmentHistory}
              historyLoading={historyLoading}
            />
          </Grid>
        )}
      </Grid>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Assessment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this assessment? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createLinkDialogOpen}
        onClose={() => {
          setCreateLinkDialogOpen(false);
          setSelectedAgentConfigId("");
          setLinkDisplayName("");
          setLinkDisplayNameTouched(false);
          setTestOverrideHumanDescription("");
          setTestAdditionalContext("");
          setSelectedControlGraphKey(null);
          setSelectedNamespaceAssetRegistry(null);
          // reset ACL create-link state
          setUserAccessControlEnabled(false);
          setMaxSessionsEnabled(false);
          setMaxSessionsPerUser(1);
          setEmailsEnabled(false);
          setEmails([]);
          setDomainsEnabled(false);
          setDomains([]);
          setCreateInsightsConfig(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Assessment Link</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
              <Autocomplete
                options={agentConfigs.filter(
                  (config) =>
                    config.organization_id ===
                    currentAssessment.organization_id
                )}
                getOptionLabel={(option) =>
                  getAgentConfigDisplayName({
                    display_name: option.display_name || null,
                    bot_name: option.bot_name || null,
                    id: option.id ?? "",
                  })
                }
                value={
                  agentConfigs.find(
                    (config) => config.id === selectedAgentConfigId
                  ) || null
                }
                onChange={(_, newValue) => {
                  setSelectedAgentConfigId(newValue?.id || "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Agent Configuration"
                    required
                    helperText="Search by display name, bot name, or ID"
                  />
                )}
                fullWidth
              />

              <TextField
                fullWidth
                label="Link Display Name"
                value={linkDisplayName}
                onChange={(e) => setLinkDisplayName(e.target.value)}
                onBlur={() => setLinkDisplayNameTouched(true)}
                error={linkDisplayNameTouched && linkDisplayName.trim() === ""}
                helperText={
                  linkDisplayNameTouched && linkDisplayName.trim() === ""
                    ? "Link Display Name cannot be empty. Give this link a friendly name (e.g., 'Q1 Sales Training Link')"
                    : "Give this link a friendly name (e.g., 'Q1 Sales Training Link')"
                }
                sx={{ mt: 3 }}
                placeholder="e.g., Q1 Sales Training Link"
                required
              />

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                Test Override Fields (Optional)
              </Typography>

              <TextField
                fullWidth
                label="Override Human Description"
                value={testOverrideHumanDescription}
                onChange={(e) =>
                  setTestOverrideHumanDescription(e.target.value)
                }
                multiline
                rows={2}
                sx={{ mb: 2 }}
                helperText="Override the human speaker's description for testing purposes"
              />

              <TextField
                fullWidth
                label="Additional Context"
                value={additionalContext}
                onChange={(e) => setTestAdditionalContext(e.target.value)}
                multiline
                rows={2}
                helperText="Add additional context for the assessment"
              />

              <TextField
                fullWidth
                label="Control Graph Key (Optional)"
                value={selectedControlGraphKey || ""}
                onChange={(e) => setSelectedControlGraphKey(e.target.value || null)}
                sx={{ mt: 3 }}
                helperText="Specify a control graph resource key (e.g., 'control_graph:ortho_intake') to override the assessment's default conversation flow"
                placeholder="control_graph:example_key"
              />

              <UserAccessControlEditor
                enabled={userAccessControlEnabled}
                onToggleEnabled={setUserAccessControlEnabled}
                maxSessionsEnabled={maxSessionsEnabled}
                onToggleMaxSessions={setMaxSessionsEnabled}
                maxSessionsPerUser={maxSessionsPerUser}
                onMaxSessionsChange={setMaxSessionsPerUser}
                // ChipEntry-based allowlists
                emailsEnabled={emailsEnabled}
                emails={emails}
                onToggleEmailsEnabled={setEmailsEnabled}
                onEmailsChange={setEmails}
                domainsEnabled={domainsEnabled}
                domains={domains}
                onToggleDomainsEnabled={setDomainsEnabled}
                onDomainsChange={setDomains}
              />
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                Assets (Optional)
              </Typography>

              <AssetPicker
                namespace={`${currentAssessment.organization_id}:assessments`}
                selectedNamespaceAssetRegistry={selectedNamespaceAssetRegistry}
                onSelectionChange={setSelectedNamespaceAssetRegistry}
              />

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                Feature Insights Configuration (Optional)
              </Typography>

              <FeatureInsightsConfigEditor
                value={createInsightsConfig}
                onChange={setCreateInsightsConfig}
                organizationId={currentAssessment.organization_id}
              />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateLinkDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateLink}
            variant="contained"
            disabled={!selectedAgentConfigId || linkDisplayName.trim() === ""}
          >
            Create Link
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editWarningDialogOpen}
        onClose={() => setEditWarningDialogOpen(false)}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            Edit Assessment Warning
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Saving changes to this assessment will create a new version. The
            current assessment will become inactive and:
          </DialogContentText>
          <Box component="ul" sx={{ mt: 2, pl: 2 }}>
            <li>
              <Typography variant="body2" component="span">
                All existing links will continue to work and point to the newly
                created version
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                All assessed sessions will remain linked to this version
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                A new assessment with your changes will be created
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                You will be redirected to the new version after saving
              </Typography>
            </li>
          </Box>
          <DialogContentText sx={{ mt: 2 }}>
            Do you want to proceed with creating a new version?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditWarningDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setEditWarningDialogOpen(false);
              handleSave();
            }}
            color="primary"
            variant="contained"
          >
            Create New Version
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Container for notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
        message="You have unsaved changes to this assessment. Are you sure you want to leave?"
      />

      {/* Delete Form Confirmation Dialog */}
      <Dialog
        open={deleteFormDialogOpen}
        onClose={() => setDeleteFormDialogOpen(false)}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="error" />
            Delete Form Schema
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the form schema for this assessment?
            This action is irreversible and will permanently remove all form
            structure and configuration.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFormDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteForm} color="error" variant="contained">
            Delete Form
          </Button>
        </DialogActions>
      </Dialog>

      {/* Control Graph Editor Dialog */}
      <Dialog
        open={controlGraphEditorOpen}
        onClose={() => setControlGraphEditorOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <AccountTreeIcon />
              Control Graph Editor
            </Box>
            <Button
              onClick={() => setControlGraphEditorOpen(false)}
              color="inherit"
            >
              Close
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: "85vh" }}>
          <ControlGraphEditor
            controlGraph={currentAssessment.control_graph || null}
            onSave={async (controlGraph) => {
              try {
                if (!currentAssessment) {
                  toast.error("Current assessment not found");
                  return;
                }

                // Build the full interview plan with the updated control_graph
                const interviewPlan = {
                  scenario: {
                    description: currentAssessment.scenario_description || "",
                    goal: currentAssessment.scenario_goal || "",
                    category: currentAssessment.scenario_category || "",
                    applicant: currentAssessment.scenario_applicant || "",
                    evaluator: currentAssessment.scenario_evaluator || "",
                    no_include: currentAssessment.no_include || "",
                  },
                  eval_criteria: currentAssessment.evaluation_criteria || [],
                  questions:
                    currentAssessment.generated_questions?.map((q: any) => {
                      return {
                        question: {
                          id: q.id || `q_${Math.random()}`,
                          question: q.topic || q.initial_question || "",
                          question_type: q.question_type || "general",
                          would_interviewer_know_answer: false,
                          is_question_multi_part_explanation: false,
                          would_answer_overlap_another_question: false,
                        },
                        dimensions: q.criteria || [],
                        rubric: q.rubric || [],
                      };
                    }) || [],
                  editor_fields: {
                    greeting:
                      currentAssessment.payload?.greeting_cue?.channels
                        ?.intent?.hints?.[0]?.value ||
                      currentAssessment.payload?.greeting ||
                      "",
                    num_questions:
                      currentAssessment.payload?.key_questions?.length || 0,
                    expected_time_minutes:
                      currentAssessment.expected_time_minutes || 15,
                    time_limit_minutes:
                      currentAssessment.time_limit_minutes || 30,
                    ask_for_questions_before_start:
                      currentAssessment.ask_for_questions_before_start ??
                      true,
                    allowed_qa_topics:
                      currentAssessment.allowed_qa_topics || undefined,
                    post_interview_steps:
                      currentAssessment.post_interview_steps || [],
                    check_for_suspicious:
                      currentAssessment.check_for_suspicious !== undefined
                        ? currentAssessment.check_for_suspicious
                        : true,
                    check_for_seeking_clarification:
                      currentAssessment.check_for_seeking_clarification !==
                      undefined
                        ? currentAssessment.check_for_seeking_clarification
                        : true,
                    max_num_user_queries_per_question:
                      currentAssessment.max_num_user_queries_per_question !==
                      undefined
                        ? currentAssessment.max_num_user_queries_per_question
                        : 3,
                    max_num_poorly_answered_questions:
                      currentAssessment.max_num_poorly_answered_questions !==
                      undefined
                        ? currentAssessment.max_num_poorly_answered_questions
                        : 2,
                    min_words_for_chat:
                      currentAssessment.min_words_for_chat !== undefined
                        ? currentAssessment.min_words_for_chat
                        : 0,
                    max_words_for_chat:
                      currentAssessment.max_words_for_chat !== undefined
                        ? currentAssessment.max_words_for_chat
                        : 0,
                    additional_guidance:
                      currentAssessment.additional_guidance || [],
                    post_insight_timeout_seconds:
                      currentAssessment.post_insight_timeout_seconds !==
                      undefined
                        ? currentAssessment.post_insight_timeout_seconds
                        : -2.0,
                    post_statement_timeout_seconds:
                      currentAssessment.post_statement_timeout_seconds !==
                      undefined
                        ? currentAssessment.post_statement_timeout_seconds
                        : -2.0,
                    without_guidance:
                      currentAssessment.without_guidance || "",
                    controversial_content_config:
                      currentAssessment.controversial_content_config || null,
                  },
                  discussion_topic: currentAssessment.discussion_topic || "",
                  criteria_context: currentAssessment.context || "",
                  assessment_processing_type:
                    currentAssessment.assessment_processing_type,
                  configuration:
                    currentAssessment.interview_configuration || {
                      min_num_turns: 5,
                      max_num_turns: 10,
                      min_num_questions_answered: 3,
                      non_job: false,
                      positive_feedback: true,
                      reflective_responses: true,
                    },
                  control_graph: controlGraph,
                };

                // This will create a new version of the assessment
                const result = await dispatch(
                  updateAssessment({
                    id: assessmentId!,
                    interview_plan: interviewPlan,
                    organization_id: currentAssessment.organization_id,
                  })
                ).unwrap();

                toast.success("Control graph saved! Redirecting to new version...");
                setControlGraphEditorOpen(false);

                // Redirect to the new assessment version
                if (result.id && result.id !== assessmentId) {
                  setTimeout(() => {
                    navigate(
                      `/dashboard/assessments/${result.organization_id}/${result.id}`,
                      { replace: true }
                    );
                  }, 500);
                } else {
                  // Refresh the current assessment
                  dispatch(
                    fetchAssessment({
                      assessmentId: assessmentId!,
                      organizationId: currentAssessment.organization_id,
                    })
                  );
                }
              } catch {
                toast.error("Failed to save control graph");
              }
            }}
            onClose={() => setControlGraphEditorOpen(false)}
          />
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default AssessmentView;
